import { useEffect, useMemo, useState, } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getIsLoading, getCourses, getCourse, getUserFieldsDefinition, getCourseTree, getSkillList } from "../selectors";
import {
    createCourseCoursesMasterAction,
    deleteCourseCoursesMasterAction,
    duplicateCourseCoursesMasterAction,
    exportCourseCoursesMasterAction,
    getCourseCoursesMasterAction,
    getCoursesCoursesMasterAction,
    getCourseTreeCoursesMasterAction,
    updateCourseCoursesMasterAction,
} from "../actions";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString } from "@icarius-localization/strings";
import { RESET_ALL, RESET_COURSE, RESET_MODULES_AND_ACTIVITIES } from "../actionTypes";
import { openDialogAction } from "@icarius-common/dialog/actions";
import { getColumnDefByPage } from "@icarius-table/columnDefs";
import paths from "@icarius-router/paths";

const useCoursesMaster = () => {

    const [treeDialogIsOpen, setTreeDialogIsOpen] = useState(false);
    const [relatedSkillsDialogIsOpen, setRelatedSkillsDialogIsOpen] = useState(false);
    const [coursePageIsOpen, setCoursePageIsOpen] = useState(false);
    const [courseFormIsOpen, setCourseFormIsOpen] = useState(false);
    const [duplicateCourseFormIsOpen, setDuplicateCourseFormIsOpen] = useState(false);
    const [importCourseDialogIsOpen, setImportCourseDialogIsOpen] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [courseInvalidFiles, setCourseInvalidFiles] = useState(null); // para el courseFormDialog

    const dispatch = useDispatch();
    const isLoading = useSelector(getIsLoading);
    const courses = useSelector(getCourses);
    const skillList = useSelector(getSkillList);
    const userFieldsDefinition = useSelector(getUserFieldsDefinition);
    const courseTree = useSelector(getCourseTree);
    const courseFormData = useSelector(getCourse);

    useEffect(() => {
        dispatch(getCoursesCoursesMasterAction());

        return () => {
            dispatch({ type: RESET_ALL });
        }
    }, [dispatch])

    const ownColumnDef = useMemo(() => {
        const auxOwnColumnDef = getColumnDefByPage(paths.coursesMaster);

        (userFieldsDefinition || []).forEach((userField) => {
            auxOwnColumnDef.push({
                headerName: userField.name,
                field: userField.name,
                filter: userField.type === "options" ? "agSetColumnFilter" : "agTextColumnFilter",
            });
        });

        return auxOwnColumnDef;
    }, [userFieldsDefinition])

    const handleOpenCreateCourseForm = () => {
        setCourseFormIsOpen(true);
    }

    const handleOpenRelatedSkillsDialog = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        const courseId = selectedRows[0].id;
        dispatch(getCourseCoursesMasterAction(courseId))
            .then((resp) => {
                if (resp.status === 200) {
                    setSelectedCourse(selectedRows[0]);
                    setRelatedSkillsDialogIsOpen(true);
                }
            })
    }

    const handleCloseRelatedSkillsDialog = () => {
        setSelectedCourse(null);
        setRelatedSkillsDialogIsOpen(false);
        dispatch({ type: RESET_COURSE });
    }

    const handleOpenUpdateCourseForm = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        const courseId = selectedRows[0].id;
        dispatch(getCourseCoursesMasterAction(courseId))
            .then((resp) => {
                if (resp.status === 200) {
                    setSelectedCourse(selectedRows[0]);
                    setCourseFormIsOpen(true);
                }
            })
    }

    const handleCloseCourseForm = () => {
        setSelectedCourse(null);
        setCourseFormIsOpen(false);
        dispatch({ type: RESET_COURSE });
    }

    const handleCreateCourse = (dataToSend) => {
        dispatch(createCourseCoursesMasterAction(dataToSend))
            .then((resp) => {
                if (resp?.status === 200) {
                    handleCloseCourseForm();
                } else {
                    if (resp?.data?.invalidFiles) {
                        setCourseInvalidFiles(resp.data.invalidFiles)
                    }
                }
            });
    }

    const handleUpdateCourse = (dataToSend) => {
        dispatch(updateCourseCoursesMasterAction(courseFormData.id, dataToSend))
            .then((resp) => {
                if (resp?.status === 200) {
                    handleCloseCourseForm();
                } else {
                    if (resp?.data?.invalidFiles) {
                        setCourseInvalidFiles(resp.data.invalidFiles)
                    }
                }
            });
    }

    const handleOpenDuplicateCourseForm = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        const courseId = selectedRows[0].id;
        dispatch(getCourseCoursesMasterAction(courseId))
            .then((resp) => {
                if (resp.status === 200) {
                    setDuplicateCourseFormIsOpen(true);
                }
            })
    }

    const handleCloseDuplicateCourseForm = () => {
        setDuplicateCourseFormIsOpen(false);
        dispatch({ type: RESET_COURSE });
    }

    const handleDuplicateCourse = (dataToSend) => {
        dispatch(duplicateCourseCoursesMasterAction(courseFormData.id, dataToSend))
            .then((resp) => {
                if (resp?.status === 200) {
                    handleCloseDuplicateCourseForm();
                }
            });
    }

    const handleOpenDeleteCourseDialog = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        if (selectedRows[0].isInUse) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("courseInUseDelete"), severity: "warning" }));
            return;
        }

        const courseId = selectedRows[0].id;
        dispatch(openDialogAction({
            title: getLocalizedString("attention"),
            msg: getLocalizedString("doYouWantToDeleteCourse"),
            onConfirm: () => dispatch(deleteCourseCoursesMasterAction(courseId)),
        }));
    }

    const handleExportCourse = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        const courseId = selectedRows[0].id;
        dispatch(exportCourseCoursesMasterAction(courseId));
    }

    const handleOpenImportCourseDialog = () => {
        setImportCourseDialogIsOpen(true);
    }

    const handleCloseImportCourseDialog = () => {
        setImportCourseDialogIsOpen(false);
    }

    const handleOpenCoursePage = (row) => {
        setSelectedCourse(row.data);
        setCoursePageIsOpen(true);
    }

    const handleCloseCoursePage = () => {
        dispatch(getCoursesCoursesMasterAction())
            .then((resp) => {
                if (resp.status === 200) {
                    setCoursePageIsOpen(false);
                    setSelectedCourse(null);
                    dispatch({ type: RESET_MODULES_AND_ACTIVITIES });
                }
            })
    }

    const handleClearInvalidFiles = () => {
        setCourseInvalidFiles(null);
    }

    const handleOpenTreeDialog = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        const courseId = selectedRows[0].id;
        dispatch(getCourseTreeCoursesMasterAction(courseId))
            .then((resp) => {
                if (resp.status === 200) {
                    setTreeDialogIsOpen(true);
                }
            });
    }

    const handleCloseTreeDialog = () => {
        setTreeDialogIsOpen(false);
    }

    const state = {
        coursePageIsOpen,
        courseFormIsOpen,
        relatedSkillsDialogIsOpen,
        duplicateCourseFormIsOpen,
        importCourseDialogIsOpen,
        treeDialogIsOpen,
        selectedCourse,
        courseInvalidFiles,
    }

    const handlers = {
        handleOpenCreateCourseForm,
        handleOpenUpdateCourseForm,
        handleCloseCourseForm,
        handleCreateCourse,
        handleUpdateCourse,
        handleOpenDuplicateCourseForm,
        handleCloseDuplicateCourseForm,
        handleOpenRelatedSkillsDialog,
        handleCloseRelatedSkillsDialog,
        handleDuplicateCourse,
        handleOpenDeleteCourseDialog,
        handleExportCourse,
        handleOpenImportCourseDialog,
        handleCloseImportCourseDialog,
        handleOpenCoursePage,
        handleCloseCoursePage,
        handleOpenTreeDialog,
        handleCloseTreeDialog,
        handleClearInvalidFiles,
    }

    return {
        ownColumnDef,
        courses,
        skillList,
        userFieldsDefinition,
        courseFormData,
        courseTree,
        isLoading,
        state,
        handlers,
    }
}

export default useCoursesMaster;